<template>
  <b-modal no-fade v-model="dialog.show" size="xl" :id="`modal-monetary-${uniqueId}`"
           :hide-footer="true" centered @close="closeModal"
           no-close-on-backdrop
  >
    <template #modal-title>
     <span v-if="loading !== true && error == null && monetary != null">
       #{{ monetary.id }}
       <b-badge :variant="monetary.type === 'DEPOSIT' ? 'success' : 'danger'" class="ml-1">{{ monetary.type }}</b-badge>
       <b-badge variant="secondary" class="ml-1">{{ monetary.status }}</b-badge>
       <template v-if="isProcessorPayment">
         <span class="ml-1 mr-1 text-muted">|</span>
         <b-badge variant="warning">
           <i class="fa-solid fa-credit-card mr-1"></i>{{ monetary.processorTransaction.paymentDetails.processorName }}
         </b-badge>
       </template>
     </span>
    </template>
    <div style="min-height: 600px">
      <div v-if="loading !== true && error == null">
        <b-tabs  v-model="dialog.tab" content-class="" align="center">
          <b-tab :title="$t('tabs.monetarys.details')" active>
            <monetary-edit-details v-bind:new-monetary="isNewMonetary" @submitted="onSubmitted" v-model="monetary"/>
          </b-tab>

          <b-tab v-if="isProcessorPayment" title="Processor">
            <div class="p-2">
              <b-alert show variant="info" class="d-flex align-items-center mb-3">
                <i class="fa-solid fa-credit-card fa-lg mr-2"></i>
                <span>This payment was processed by <strong>{{ monetary.processorTransaction.paymentDetails.processorName }}</strong>
                  via <strong>{{ monetary.processorTransaction.paymentDetails.paymentMethod }}</strong></span>
              </b-alert>

              <b-row>
                <b-col cols="4" class="mt-1">
                  <small class="text-muted d-block">Processor Transaction ID</small>
                  <span>{{ monetary.processorTransaction.id }}</span>
                </b-col>
                <b-col cols="4" class="mt-1">
                  <small class="text-muted d-block">External Transaction ID</small>
                  <span class="text-break">{{ monetary.processorTransaction.extTransactionId }}</span>
                </b-col>
                <b-col cols="4" class="mt-1">
                  <small class="text-muted d-block">Processor Status</small>
                  <b-badge :variant="processorStatusVariant">{{ monetary.processorTransaction.status }}</b-badge>
                </b-col>

                <b-col cols="4" class="mt-2">
                  <small class="text-muted d-block">{{ $t('columns.amount') }}</small>
                  <span>{{ monetary.processorTransaction.amount }} {{ monetary.processorTransaction.currency }}</span>
                </b-col>
                <b-col cols="4" class="mt-2">
                  <small class="text-muted d-block">Payment Method</small>
                  <span>{{ monetary.processorTransaction.paymentDetails.paymentMethod }}</span>
                </b-col>
                <b-col cols="4" class="mt-2">
                  <small class="text-muted d-block">Processor Name</small>
                  <span>{{ monetary.processorTransaction.paymentDetails.processorName }}</span>
                </b-col>

                <b-col cols="6" class="mt-2">
                  <small class="text-muted d-block">{{ $t('columns.registrationTime') }}</small>
                  <span>{{ monetary.processorTransaction.registrationTime | momentDateTime }}</span>
                </b-col>
                <b-col cols="6" class="mt-2">
                  <small class="text-muted d-block">Last Update</small>
                  <span>{{ monetary.processorTransaction.lastUpdateTime | momentDateTime }}</span>
                </b-col>
              </b-row>

              <!-- Messages Log -->
              <div v-if="monetary.processorTransaction.messages && monetary.processorTransaction.messages.length > 0" class="mt-3">
                <hr class="my-2"/>
                <small class="text-muted d-block mb-1">Processor Messages</small>
                <div v-for="(msg, idx) in monetary.processorTransaction.messages" :key="idx"
                     class="bg-light p-2 mb-1 rounded" style="font-size: 0.8rem; word-break: break-all;">
                  {{ msg }}
                </div>
              </div>
            </div>
          </b-tab>

        </b-tabs>
      </div>
      <div class="text-center position-absolute d-block w-100" style="top:50%"
           v-else-if="loading !== true && error != null"
      >
        <b-alert show variant="danger">
          <i class="fa-sharp fa-solid fa-triangle-exclamation"></i>
          {{ error }}
        </b-alert>
      </div>
      <div class="text-center position-absolute d-block w-100" style="top:50%" v-else>
        <b-spinner>{{ $t('operations.loading') }}</b-spinner>
      </div>
    </div>
  </b-modal>
</template>

<script>
import mixinBase from '@/components/mixin/mixinBase';
import mixinNotifications from '@/components/mixin/mixinNotifications';
import { mapActions } from 'vuex';
import MonetaryEditDetails from '@/components/partial/monetaries/MonetaryEditDetails';
import Monetary from '@/model/monetary/Monetary';

export default {
  name: 'monetaryModal',
  components: { MonetaryEditDetails },
  mixins: [mixinBase, mixinNotifications],
  data: () => ({
    dialog: {
      show: false,
      tab: 0,

    },
    uniqueId: 0,
    loading: true,
    error: null,
    id: null,
    isNewMonetary: false,
    monetary: {},
  }),
  computed: {
    isProcessorPayment() {
      return this.monetary != null
        && this.monetary.processorTransaction != null
        && this.monetary.processorTransaction.paymentDetails != null;
    },
    processorStatusVariant() {
      if (!this.isProcessorPayment) return 'secondary';
      const status = this.monetary.processorTransaction.status;
      if (status === 'Paid' || status === 'APPROVED') return 'success';
      if (status === 'NotPaid' || status === 'DECLINED') return 'danger';
      if (status === 'Pending' || status === 'PENDING') return 'warning';
      return 'secondary';
    }
  },

  watch: {
    queryParams: {
      handler() {
        if (this.queryParams().monetaryId == null) {
          this.closeModal();
          return;
        }
        if (this.queryParams().monetaryId !== this.id) {
          this.showModal(this.queryParams().monetaryId);
        }
      },
      deep: true
    }
  },
  created() {
    this.uniqueId = this._uid;
    if (this.queryParams().monetaryId != null) {
      this.showModal(this.queryParams().monetaryId);
    }
  },
  methods: {
    ...mapActions('monetary', ['getMonetaryById']),
    showModal(id) {
      this.id = id;
      this.loading = true;
      this.dialog.show = true;
      this.dialog.tab = 0;
      this.isNewMonetary = this.id == null || this.id === 0;
      if (!this.isNewMonetary) {
        this.refreshData();
      } else {
        this.monetary = Monetary();
        this.error = null;
        this.loading = false;
      }
      this.pushState({
        monetaryId: id,
        tab: 0,
      });
    },
    closeModal() {
      this.dialog.show = false;
      this.monetary = {};
      this.onDialogClose();
    },
    onDialogClose() {
      this.id = null;
      this.pushState({
        monetaryId: null,
        tab: null,
      });
    },
    refreshData() {
      let $this = this;
      this.getMonetaryById(this.id)
          .then(monetary => {
            $this.monetary = monetary;
            $this.error = null;
            $this.loading = false;
          }, err => {
            $this.error = $this.getErrorDescription(err);
            $this.loading = false;
          });
    },
    onSubmitted(server) {
      if (server.id != null)
        this.selected = server.id;
      this.refreshData()
      this.dialog.tab = 0;
    }
  }
};
</script>

<style scoped>

</style>
